// ─── Stored model ─────────────────────────────────────────────────────────────

/** How a project gets its design source. Chosen once the details are saved; fixes what the Source and Prepare stages ask for. */
export const WORKFLOW_TYPES = ['EXISTING_TEMPLATE', 'INDESIGN_FILE', 'GENERATE_TEMPLATE'] as const
export type WorkflowType = (typeof WORKFLOW_TYPES)[number]

/**
 * The shared stages. `GENERATE` exists only for “Generate template” projects: between reviewing the extracted content and the client
 * workspace the design is generated, regenerated and customized with the AI, then shared. The other workflows skip it.
 */
export const PROJECT_STAGES = ['DETAILS', 'SOURCE', 'PREPARE', 'GENERATE', 'CLIENT_WORKSPACE', 'APPROVAL', 'FINALIZE'] as const
export type ProjectStage = (typeof PROJECT_STAGES)[number]

export const PROJECT_STATUSES = [
  'DRAFT',
  'IN_PROGRESS',
  'IN_REVIEW',
  'REVISION_REQUESTED',
  'APPROVED',
  'COMPLETED',
  'CANCELLED',
] as const
export type ProjectStatus = (typeof PROJECT_STATUSES)[number]

export type ApprovalMode = 'DIRECT' | 'TEAM'

/**
 * The exact design a client has put forward for approval. `variationId` is null for a shared source or working copy
 * used as-is; `workingCopyId` names the project-specific copy when that is what was chosen.
 */
export interface Candidate {
  designVersionId: string
  variationId: string | null
  workingCopyId: string | null
}

/** Progress of the simulated InDesign validation and conversion. */
export type PreparationStatus = 'QUEUED' | 'VALIDATING' | 'CONVERTING' | 'READY' | 'FAILED'
export interface Preparation {
  status: PreparationStatus
  detail: string
  updatedAt: string
}

/** Projects.Projects. The supplier is not stored: it is the client's one `SUPPLIER_CLIENT` link. */
export interface ProjectRecord {
  id: string
  /** The client the project is for: the account mapped to it (see `clientAccountOf`). */
  clientAccountId: string
  /** The account of the person who created the project, which is the backend's own `accountId` for it (not the client's). */
  ownerAccountId: string
  name: string
  description: string
  workflowType: WorkflowType | null
  status: ProjectStatus
  currentStage: ProjectStage
  createdByUserId: string
  /** When the master was shared with the client. Null until then. */
  assignedClientAt: string | null
  masterTemplateId: string | null
  latestVersionNumber: number
  approvalMode: ApprovalMode | null
  candidate: Candidate | null
  businessType: string
  location: string
  /** The formats as one line ("PDF, PNG"), for places that show them in words. */
  outputFormat: string
  /** The formats the backend knows: PDF, PNG, JPG, IDML. Missing on older records. */
  outputFormats?: string[]
  /** Artwork size as the backend wants it: width x height in inches, e.g. "8.5x11". Older records hold a name like "Letter". */
  pageSize: string
  /** The status text the backend holds for the project, sent back unchanged when the project is updated. */
  apiStatus?: string
  /** The SKU ID given when the design was approved. Empty until then. */
  sku?: string
  /** Where the server keeps the pictures of the project's latest generated design (one per page), as the project list sends them. The first is the project's thumbnail. */
  generatedImagePaths?: string[]
  /** The server's task that is making the design, set when Review starts it and followed by the Generate step until it is done. Kept for this visit only. */
  generationTaskId?: string
  /** Which of the three reviews the server holds as done: the asset review, the art content and the creative brief. Missing until the server has sent them. */
  workflowProgress?: { assetReview: boolean; artContent: boolean; creativeBrief: boolean }
  /** The server's task that is reading a changed client brief again into a new creative brief. Followed by the project page until it is done. */
  briefTaskId?: string
  /** Which review tab to open when Review is next shown (set when the new creative brief is ready). */
  reviewFocus?: 'creativeBrief'
  /** What the client asked for, in their words. */
  clientBrief: string
  preparation: Preparation | null
  /** Highest item number ever issued for this project's assets, so a code is never handed out twice. */
  lastItemNumber?: number
  createdAt: string
  updatedAt: string
}

/** Projects.ProjectUsers: explicit assignment of one person to one project. */
export interface ProjectUser {
  id: string
  projectId: string
  userId: string
  assignedAt: string
}

export const EVENT_TYPES = [
  'PROJECT_CREATED',
  'PROJECT_UPDATED',
  'WORKFLOW_CHOSEN',
  'SOURCE_UPLOADED',
  'PREPARATION_STARTED',
  'PREPARATION_FINISHED',
  'CONCEPTS_GENERATED',
  'MASTER_SAVED',
  'MASTER_SHARED',
  'VARIATIONS_GENERATED',
  'WORKING_COPY_SAVED',
  'CANDIDATE_SELECTED',
  'WORKSPACE_REOPENED',
  'APPROVAL_ROUTE_CHOSEN',
  'PARTICIPANTS_ASSIGNED',
  'REVIEW_SHARED',
  'REVIEW_WITHDRAWN',
  'COMMENT_ADDED',
  'REVISION_REQUESTED',
  'APPROVED',
  'DELIVERABLE_READY',
  'PROJECT_COMPLETED',
  'PROJECT_CANCELLED',
] as const
export type EventType = (typeof EVENT_TYPES)[number]

/** Append-only history. Nothing in the app edits or removes an event. */
export interface ProjectEvent {
  id: string
  projectId: string
  actorUserId: string
  type: EventType
  summary: string
  createdAt: string
  data?: Record<string, unknown>
}
